import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import {
  Home,
  Building2,
  Store,
  Menu,
  Palette,
  BarChart3,
  MessageSquare,
  QrCode,
  Settings,
  LogOut,
  ClipboardList,
  Facebook,
  Users,
  Calculator,
  Gift,
  TrendingUp,
  Clock,
  Crown,
  Phone,
  DollarSign,
  Truck,
  Shield,
  TableProperties,
  ChefHat,
  FileText,
  CalendarDays,
  LayoutTemplate,
  PenTool
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';
import type { StaffRole } from '@/types/index';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { MobileBottomNav } from './MobileBottomNav';
import { TrialWarningBanner } from './subscription/TrialWarningBanner';
import { OfflineBanner } from '@/components/ui/OfflineBanner';

interface DashboardLayoutProps {
  children: React.ReactNode;
  hideSidebar?: boolean;
}

interface NavigationItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

// Define permissions for each role (fallback for old system)
const ROLE_PERMISSIONS: Record<StaffRole, string[]> = {
  OWNER: ['overview', 'business_profile', 'branches', 'menu_management', 'orders', 'waiting_list', 'reservations', 'delivery_management', 'inventory_management', 'staff_management', 'loyalty_program', 'customer_communication', 'marketing_promotions', 'financial_reports', 'analytics', 'advanced_analytics', 'feedback', 'audit_logs', 'customization', 'menu_builder', 'menu_designer', 'qr_codes', 'tables', 'kitchen', 'meta_pixel', 'settings', 'subscription'],
  MANAGER: ['overview', 'menu_management', 'orders', 'waiting_list', 'reservations', 'delivery_management', 'inventory_management', 'analytics', 'feedback', 'qr_codes', 'tables', 'kitchen', 'menu_builder', 'menu_designer'],
  CASHIER: ['overview', 'orders', 'waiting_list', 'reservations', 'kitchen'],
  VIEWER: ['overview', 'analytics', 'feedback']
};

const PERMISSION_TO_HREF: Record<string, string> = {
  overview: '/dashboard',
  business_profile: '/dashboard/business-profile',
  branches: '/dashboard/branches',
  menu_management: '/dashboard/menu',
  orders: '/dashboard/orders',
  waiting_list: '/dashboard/waiting-list',
  reservations: '/dashboard/reservations',
  delivery_management: '/dashboard/delivery',
  inventory_management: '/dashboard/inventory',
  staff_management: '/dashboard/staff-management',
  loyalty_program: '/dashboard/loyalty',
  customer_communication: '/dashboard/communication',
  marketing_promotions: '/dashboard/marketing',
  financial_reports: '/dashboard/financial',
  analytics: '/dashboard/analytics',
  advanced_analytics: '/dashboard/advanced-analytics',
  feedback: '/dashboard/feedback',
  audit_logs: '/dashboard/audit-logs',
  customization: '/dashboard/customization',
  menu_builder: '/dashboard/menu-builder',
  menu_designer: '/dashboard/menu-designer',
  qr_codes: '/dashboard/qr-codes',
  tables: '/dashboard/tables',
  kitchen: '/dashboard/kitchen',
  meta_pixel: '/dashboard/meta-pixel',
  settings: '/dashboard/settings',
  subscription: '/dashboard/subscription'
};

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children, hideSidebar = false }) => {
  const { signOut, isStaff, staffRole, user, staffSession } = useAuth();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const previouslyFocusedElement = useRef<HTMLElement | null>(null);
  const drawerRef = useRef<HTMLDivElement | null>(null);
  const { isAdmin } = useIsAdmin();

  // Get current user role (staff or owner)
  const currentRole: StaffRole = isStaff ? (staffRole as StaffRole) : 'OWNER';
  
  // Calculate permissions synchronously to prevent layout shifts
  const allowedPermissions = React.useMemo(() => {
    if (isStaff && staffSession?.access_permissions) {
      return Object.keys(staffSession.access_permissions).filter(
        key => staffSession.access_permissions[key as keyof typeof staffSession.access_permissions]
      );
    }
    // Fallback to role-based permissions
    return ROLE_PERMISSIONS[currentRole] || [];
  }, [isStaff, staffSession, currentRole]);

  // Filter navigation sections based on role permissions
  const filterNavigationItems = (items: NavigationItem[]) => {
    return items.filter(item => {
      // Find permission key for this href
      const permissionKey = Object.keys(PERMISSION_TO_HREF).find(
        key => PERMISSION_TO_HREF[key] === item.href
      );
      return permissionKey && allowedPermissions.includes(permissionKey);
    });
  };

  // Sidebar content renderer (reused for desktop and mobile drawer)
  const renderSidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4">
        <h1 className="text-2xl font-bold text-gray-900">{t('dashboard.title')}</h1>
        {isStaff && staffSession && (
          <p className="text-sm text-gray-500 mt-1">Staff: {staffSession.username}</p>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-6 overflow-y-auto px-0">
        {navigationSections.map((section, sectionIndex) => (
          <div key={section.title}>
            {sectionIndex > 0 && <Separator className="my-4" />}
            <div className="px-3 mb-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                {section.title}
              </h3>
            </div>
            <div className="space-y-1 px-3">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={cn(
                      "flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors",
                      isActive
                        ? "bg-[#6366F1] text-white"
                        : "text-gray-600 hover:bg-[#F3F4F6] hover:text-gray-900"
                    )}
                  >
                    <Icon className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />
                    <span className="truncate">{item.name}</span>
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <Separator className="my-4" />

      <div className="p-2">
        <LanguageSwitcher />
      </div>

      {/* Logout Button */}
      <div className="p-2">
        <Button
          variant="ghost"
          onClick={signOut}
          className="w-full justify-start text-gray-600 hover:bg-[#F3F4F6] hover:text-gray-900 px-3 py-2"
        >
          <LogOut className={`h-4 w-4 ${isRTL ? 'ml-3' : 'mr-3'}`} />
          <span>{t('dashboard.logout')}</span>
        </Button>
      </div>
    </div>
  );

  // Focus trap & accessibility handlers for mobile drawer
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isSidebarOpen) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setIsSidebarOpen(false);
        return;
      }

      if (e.key === 'Tab') {
        // Simple focus trap: keep focus inside drawer
        const container = drawerRef.current;
        if (!container) return;
        const focusable = Array.from(container.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
        )).filter(el => !el.hasAttribute('disabled'));
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (!document.activeElement) {
          first.focus();
          e.preventDefault();
          return;
        }

        if (e.shiftKey && document.activeElement === first) {
          last.focus();
          e.preventDefault();
        } else if (!e.shiftKey && document.activeElement === last) {
          first.focus();
          e.preventDefault();
        }
      }
    };

    if (isSidebarOpen) {
      // Save previously focused element
      previouslyFocusedElement.current = document.activeElement as HTMLElement | null;
      // Focus first focusable element in drawer
      setTimeout(() => {
        const container = drawerRef.current;
        if (container) {
          const first = container.querySelector<HTMLElement>(
            'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])'
          );
          first?.focus();
        }
      }, 0);

      // Lock body scroll
      document.body.style.overflow = 'hidden';
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
      // Restore focus
      if (!isSidebarOpen && previouslyFocusedElement.current) {
        try { previouslyFocusedElement.current.focus(); } catch (e) { /* ignore */ }
      }
    };
  }, [isSidebarOpen]);

  const navigationSections = [
    {
      title: t('dashboard.navigation.sections.overview'),
      items: filterNavigationItems([
        { name: t('dashboard.navigation.overview'), href: '/dashboard', icon: Home },
        { name: t('dashboard.navigation.business_profile'), href: '/dashboard/business-profile', icon: Building2 },
        { name: t('dashboard.navigation.branches'), href: '/dashboard/branches', icon: Store },
      ])
    },
    {
      title: t('dashboard.navigation.sections.operations'),
      items: filterNavigationItems([
        { name: t('dashboard.navigation.menu_management'), href: '/dashboard/menu', icon: Menu },
        { name: t('dashboard.navigation.orders'), href: '/dashboard/orders', icon: ClipboardList },
        { name: t('dashboard.navigation.kitchen'), href: '/dashboard/kitchen', icon: ChefHat },
        { name: t('dashboard.navigation.waiting_list'), href: '/dashboard/waiting-list', icon: Clock },
        { name: t('dashboard.navigation.reservations'), href: '/dashboard/reservations', icon: CalendarDays },
        { name: t('dashboard.navigation.delivery_management'), href: '/dashboard/delivery', icon: Truck },
        { name: t('dashboard.navigation.inventory'), href: '/dashboard/inventory', icon: Calculator },
      ])
    },
    {
      title: t('dashboard.navigation.sections.management'),
      items: filterNavigationItems([
        { name: t('dashboard.navigation.staff_management'), href: '/dashboard/staff-management', icon: Users },
        { name: t('dashboard.navigation.loyalty_program'), href: '/dashboard/loyalty', icon: Gift },
        { name: t('dashboard.navigation.customer_communication'), href: '/dashboard/communication', icon: Phone },
        { name: t('dashboard.navigation.marketing_promotions'), href: '/dashboard/marketing', icon: TrendingUp },
        { name: t('dashboard.navigation.financial_reports'), href: '/dashboard/financial', icon: DollarSign },
        { name: t('dashboard.navigation.subscription'), href: '/dashboard/subscription', icon: Crown },
      ])
    },
    {
      title: t('dashboard.navigation.sections.analytics'),
      items: filterNavigationItems([
        { name: t('dashboard.navigation.analytics'), href: '/dashboard/analytics', icon: BarChart3 },
        { name: t('dashboard.navigation.advanced_analytics'), href: '/dashboard/advanced-analytics', icon: TrendingUp },
        { name: t('dashboard.navigation.feedback'), href: '/dashboard/feedback', icon: MessageSquare },
        { name: t('dashboard.navigation.audit_logs'), href: '/dashboard/audit-logs', icon: FileText },
      ])
    },
    {
      title: t('dashboard.navigation.sections.customization'),
      items: filterNavigationItems([
        { name: t('dashboard.navigation.customization'), href: '/dashboard/customization', icon: Palette },
        { name: t('dashboard.navigation.menu_builder'), href: '/dashboard/menu-builder', icon: LayoutTemplate },
        { name: t('dashboard.navigation.menu_designer'), href: '/dashboard/menu-designer', icon: PenTool },
        { name: t('dashboard.navigation.qr_codes'), href: '/dashboard/qr-codes', icon: QrCode },
        { name: t('dashboard.navigation.tables'), href: '/dashboard/tables', icon: TableProperties },
        { name: t('dashboard.navigation.meta_pixel'), href: '/dashboard/meta-pixel', icon: Facebook },
        { name: t('dashboard.navigation.settings'), href: '/dashboard/settings', icon: Settings },
      ])
    },
    ...(isAdmin ? [{
      title: 'Platform Admin',
      items: [
        { name: 'Subscription Manager', href: '/dashboard/admin/subscription', icon: Shield },
        { name: 'Visual Mapper', href: '/dashboard/admin/visual-mapper', icon: PenTool }
      ]
    }] : [])
  ].filter(section => section.items.length > 0); // Only show sections with allowed items

  return (
    <div className="min-h-screen bg-[#F3F4F6] flex w-full overflow-x-hidden">
      {/* Desktop sidebar (visible md+) */}
      {!hideSidebar && (
        <div className="hidden md:flex w-[280px] bg-white border-r border-gray-200 flex-col p-4 overflow-y-auto">
          {renderSidebarContent()}
        </div>
      )}

      {/* Mobile sidebar drawer - triggered from MobileBottomNav "More" menu */}
      {!hideSidebar && isSidebarOpen && (
        <div className="fixed inset-0 z-[9999] md:hidden" aria-hidden={!isSidebarOpen}>
          {/* overlay */}
          <div className="absolute inset-0 bg-black/40" onClick={() => setIsSidebarOpen(false)} />

          {/* Panel */}
          <div
            id="mobile-sidebar"
            ref={drawerRef}
            role="dialog"
            aria-modal="true"
            aria-label={t('dashboard.navigation') || 'Sidebar'}
            className={cn(
              'absolute top-0 h-full w-4/5 max-w-xs bg-white shadow-xl transform transition-transform z-[10000]',
              isRTL ? 'right-0' : 'left-0'
            )}
          >
            {renderSidebarContent()}
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        <main
          className={cn(
            "flex-1 min-w-0",
            hideSidebar ? "p-2 sm:p-4" : "p-6",
            // Add bottom padding on mobile for bottom nav
            !hideSidebar && "pb-20 md:pb-6"
          )}
          aria-hidden={isSidebarOpen && !hideSidebar}
        >
          <TrialWarningBanner />
          <OfflineBanner />
          {children}
        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {!hideSidebar && (
        <MobileBottomNav onMoreClick={() => setIsSidebarOpen(true)} />
      )}
    </div>
  );
};
